import { Button, DataList, HStack, Stack, Text } from '@chakra-ui/react'
import { LuPencil, LuX } from 'react-icons/lu'
import {
  formatIsoDateDE,
  formatValue,
  isBaselineDate,
  type PlannedValue,
  type ValueKind,
} from '@/helper/valueChange.helper'

type ServiceValueSummaryProps = {
  kind: ValueKind
  serviceLabel: string
  /** The value in effect today; null when nothing is set yet. */
  activeValue: number | null
  /** Since when the active value has been in effect. */
  activeSince?: string | null
  /** Changes scheduled for a later date, earliest first. */
  planned: PlannedValue[]
  /** The planned change currently loaded into the input, if any. */
  editingId?: number | null
  onEdit?: (planned: PlannedValue) => void
  onDrop?: (planned: PlannedValue) => void
  disabled?: boolean
  /** Fixed values have no planned changes and no actions. */
  fixed?: boolean
  /** Label of the value row; a planning year that hasn't started names its own value (SIB-928). */
  activeLabel?: string
  /** Replaces the "(seit …)" note after the value, e.g. "(Wert aus 2026)". */
  activeNote?: string | null
}

/**
 * "Aktuell" and "Gültig ab" for one service (SIB-941).
 *
 * The active value and a planned value must never be confusable, so both carry an explicit label
 * and the planned one always names its date. Only planned (future) versions offer edit and drop;
 * a version whose date has arrived is history and gets no action here.
 */
export function ServiceValueSummary({
  kind,
  serviceLabel,
  activeValue,
  activeSince,
  planned,
  editingId = null,
  onEdit,
  onDrop,
  disabled = false,
  fixed = false,
  activeLabel = 'Aktuell',
  activeNote,
}: ServiceValueSummaryProps) {
  return (
    <DataList.Root orientation="vertical">
      <DataList.Item>
        <DataList.ItemLabel>{activeLabel}</DataList.ItemLabel>
        <DataList.ItemValue>
          <Text as="span" data-testid={`active-value-${serviceLabel}`}>
            {activeValue === null ? 'Noch kein Wert hinterlegt' : formatValue(kind, activeValue)}
          </Text>
          {/* ItemValue is a flex row, which drops a plain space, so the note keeps its own gap. */}
          {activeValue !== null && activeNote && (
            <Text as="span" color="fg.muted" ms="1">
              {activeNote}
            </Text>
          )}
          {activeValue !== null && activeNote === undefined && activeSince && (
            <Text as="span" color="fg.muted" ms="1">
              {isBaselineDate(activeSince)
                ? '(von Anfang an)'
                : `(seit ${formatIsoDateDE(activeSince)})`}
            </Text>
          )}
          {fixed && (
            <>
              {' '}
              <Text as="span" color="fg.muted">
                (Festpreis)
              </Text>
            </>
          )}
        </DataList.ItemValue>
      </DataList.Item>

      {planned.map(change => {
        const isEditing = editingId === change.id
        return (
          <DataList.Item key={change.id}>
            <DataList.ItemLabel>Gültig ab {formatIsoDateDE(change.validFrom)}</DataList.ItemLabel>
            <DataList.ItemValue>
              <Stack gap={1}>
                <Text as="span" data-testid={`planned-value-${serviceLabel}`}>
                  {formatValue(kind, change.value)}
                  {isEditing && (
                    <>
                      {' '}
                      <Text as="span" color="fg.muted">
                        (wird bearbeitet)
                      </Text>
                    </>
                  )}
                </Text>
                {(onEdit || onDrop) && (
                  <HStack gap={2} flexWrap="wrap">
                    {onEdit && (
                      <Button
                        variant="outline"
                        onClick={() => onEdit(change)}
                        disabled={disabled || isEditing}
                        aria-label={`Geplante Änderung ${serviceLabel} ab ${formatIsoDateDE(change.validFrom)} bearbeiten`}
                      >
                        <LuPencil />
                        Bearbeiten
                      </Button>
                    )}
                    {onDrop && (
                      <Button
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => onDrop(change)}
                        disabled={disabled}
                        aria-label={`Geplante Änderung ${serviceLabel} ab ${formatIsoDateDE(change.validFrom)} verwerfen`}
                      >
                        <LuX />
                        Verwerfen
                      </Button>
                    )}
                  </HStack>
                )}
              </Stack>
            </DataList.ItemValue>
          </DataList.Item>
        )
      })}
    </DataList.Root>
  )
}
